/**
 * Provider Stripe Product Creator
 * Form for creating new Stripe Products with pricing configuration
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Divider,
  InputAdornment,
  Alert,
  CircularProgress,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Switch,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { AppButton, AppIconButton, AppTextField } from '../design-system';
import {
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import * as dt from '@cnapse/shared/data.types.js';
import type { CreateStripeProductRequest } from '@cnapse/shared/schemas.js';
import { providerStripeService } from '../../services/ProviderStripeService';
import { PRODUCT_CREATOR_MODULE_LABELS, AXON_GATEWAY_LIMIT_LABEL, STRIPE_MODULE_FEATURE_KEYS } from '@cnapse/shared';

interface ProviderStripeProductCreatorProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ProviderStripeProductCreator: React.FC<ProviderStripeProductCreatorProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [createdProductId, setCreatedProductId] = useState<string>('');
  
  // Trial period state
  const [enableTrial, setEnableTrial] = useState(false);
  const [trialDays, setTrialDays] = useState<number>(30);

  type ModulePricing = { enabled: boolean; price: number };

  const [modules, setModules] = useState<Record<(typeof STRIPE_MODULE_FEATURE_KEYS)[number], ModulePricing>>({
    cci_vision: { enabled: false, price: 0 },
    spatial_connect: { enabled: false, price: 0 },
    spatial_uwb: { enabled: false, price: 0 },
  });

  // Form state
  const [formData, setFormData] = useState<CreateStripeProductRequest>({
    name: '',
    description: '',
    monthlyBasePrice: 500,
    billingCycle: dt.BILLING_CYCLE.Monthly,
    baseUsers: 10,
    baseContainers: 100,
    baseWafers: 1000,
    baseAxonGateways: 5,
    baseStorageTb: 1.0,
    pricePerUser: 20,
    pricePerContainer: 5,
    pricePer1000Wafers: 10,
    pricePerAxonGateway: 50,
    pricePerTb: 30,
    customerManagedEncryption: false,
    encryptionFee: 0,
  });

  const handleChange = <K extends keyof CreateStripeProductRequest>(
    field: K,
    value: CreateStripeProductRequest[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Validate trial period if enabled
    if (enableTrial && (!trialDays || trialDays < 1)) {
      setError('Trial period must be at least 1 day');
      setLoading(false);
      return;
    }

    try {
      const requestData: CreateStripeProductRequest = {
        ...formData,
        ...(enableTrial && { trialPeriodDays: trialDays }),
        features: {
          cci_vision: modules.cci_vision,
          spatial_connect: modules.spatial_connect,
          spatial_uwb: modules.spatial_uwb,
        },
      };
      
      const response = await providerStripeService.createStripeProduct(requestData);
      setCreatedProductId(response.data.id);
      setSuccess(true);
      
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create Stripe Product');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        description: '',
        monthlyBasePrice: 500,
        billingCycle: dt.BILLING_CYCLE.Monthly,
        baseUsers: 10,
        baseContainers: 100,
        baseWafers: 1000,
        baseAxonGateways: 5,
        baseStorageTb: 1.0,
        pricePerUser: 20,
        pricePerContainer: 5,
        pricePer1000Wafers: 10,
        pricePerAxonGateway: 50,
        pricePerTb: 30,
        customerManagedEncryption: false,
        encryptionFee: 0,
      });
      setError(null);
      setSuccess(false);
      setCreatedProductId('');
      setEnableTrial(false);
      setTrialDays(30);
      setModules({
        cci_vision: { enabled: false, price: 0 },
        spatial_connect: { enabled: false, price: 0 },
        spatial_uwb: { enabled: false, price: 0 },
      });
      onClose();
    }
  };

  const getBillingPeriodLabel = () => {
    return formData.billingCycle === dt.BILLING_CYCLE.Yearly ? '/year' : '/month';
  };

  const calculateMonthlyTotal = () => {
    const modulesTotal = Object.values(modules).reduce(
      (sum, module) => sum + (module.enabled ? module.price : 0),
      0,
    );
    return formData.monthlyBasePrice + (formData.encryptionFee || 0) + modulesTotal;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Create Product</Typography>
          <AppIconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </AppIconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" icon={<InfoIcon />}>
            Create a Stripe Product with custom pricing. This will be stored in Stripe and can be assigned to tenants.
          </Alert>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Collapse in={success}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Product created successfully! Product ID: <strong>{createdProductId}</strong>
          </Alert>
        </Collapse>

        <Stack spacing={3}>
          {/* Basic Information */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <AppTextField
                fullWidth
                required
                label="Plan Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Enterprise Plan - ACME Corp"
                helperText="This name will appear in Stripe Dashboard"
              />
              <AppTextField
                fullWidth
                label="Description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                multiline
                rows={2}
                placeholder="Optional description for internal reference"
              />
            </Stack>
          </Box>

          {/* Base Pricing */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Base Pricing
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Billing Cycle</InputLabel>
                  <Select
                    value={formData.billingCycle}
                    onChange={(e) => handleChange('billingCycle', e.target.value as dt.BILLING_CYCLE)}
                    label="Billing Cycle"
                  >
                    <MenuItem value={dt.BILLING_CYCLE.Monthly}>Monthly</MenuItem>
                    <MenuItem value={dt.BILLING_CYCLE.Yearly}>Yearly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <AppTextField
                  fullWidth
                  required
                  type="number"
                  label={`Base Price ${formData.billingCycle === dt.BILLING_CYCLE.Yearly ? '(Yearly)' : '(Monthly)'}`}
                  value={formData.monthlyBasePrice}
                  onChange={(e) => handleChange('monthlyBasePrice', parseFloat(e.target.value))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    endAdornment: <InputAdornment position="end">{getBillingPeriodLabel()}</InputAdornment>,
                  }}
                  helperText={`Fixed ${formData.billingCycle === dt.BILLING_CYCLE.Yearly ? 'yearly' : 'monthly'} charge before overage fees`}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Trial Period */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Trial Period
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableTrial}
                    onChange={(e) => setEnableTrial(e.target.checked)}
                    disabled={loading}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Enable Free Trial</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Customers will get free access for the trial period before being charged
                    </Typography>
                  </Box>
                }
              />
              
              {enableTrial && (
                <AppTextField
                  fullWidth
                  required
                  type="number"
                  label="Trial Period (Days)"
                  value={trialDays}
                  onChange={(e) => setTrialDays(parseInt(e.target.value) || 0)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">days</InputAdornment>,
                  }}
                  helperText="Number of days customers can use the service for free"
                  inputProps={{ min: 1, max: 365 }}
                  error={enableTrial && (!trialDays || trialDays < 1)}
                />
              )}
            </Stack>
          </Box>

          {/* Base Quotas */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Base Quotas (Included in Base Price)
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <AppTextField
                  fullWidth
                  required
                  type="number"
                  label="Base Users"
                  value={formData.baseUsers}
                  onChange={(e) => handleChange('baseUsers', parseInt(e.target.value))}
                  helperText="Number of users included"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <AppTextField
                  fullWidth
                  required
                  type="number"
                  label="Base Containers"
                  value={formData.baseContainers}
                  onChange={(e) => handleChange('baseContainers', parseInt(e.target.value))}
                  helperText="Number of containers included"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <AppTextField
                  fullWidth
                  required
                  type="number"
                  label="Base Wafers"
                  value={formData.baseWafers}
                  onChange={(e) => handleChange('baseWafers', parseInt(e.target.value))}
                  helperText="Number of wafers included"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <AppTextField
                  fullWidth
                  required
                  type="number"
                  label={`Base ${AXON_GATEWAY_LIMIT_LABEL}`}
                  value={formData.baseAxonGateways}
                  onChange={(e) => handleChange('baseAxonGateways', parseInt(e.target.value, 10))}
                  helperText="Axon gateways included in base price"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <AppTextField
                  fullWidth
                  required
                  type="number"
                  label="Base Storage (TB)"
                  value={formData.baseStorageTb}
                  onChange={(e) => handleChange('baseStorageTb', parseFloat(e.target.value))}
                  helperText="Storage in terabytes included"
                  inputProps={{ min: 0.1, step: 0.1 }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Overage Pricing */}
          <Box>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Overage Pricing (Per Unit Above Base)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <AppTextField
                      fullWidth
                      type="number"
                      label="Price per User"
                      value={formData.pricePerUser || 0}
                      onChange={(e) => handleChange('pricePerUser', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        endAdornment: <InputAdornment position="end">/user</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <AppTextField
                      fullWidth
                      type="number"
                      label="Price per Container"
                      value={formData.pricePerContainer || 0}
                      onChange={(e) => handleChange('pricePerContainer', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        endAdornment: <InputAdornment position="end">/container</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <AppTextField
                      fullWidth
                      type="number"
                      label="Price per 1000 Wafers"
                      value={formData.pricePer1000Wafers || 0}
                      onChange={(e) => handleChange('pricePer1000Wafers', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        endAdornment: <InputAdornment position="end">/1000 wafers</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <AppTextField
                      fullWidth
                      type="number"
                      label="Price per Axon Gateway"
                      value={formData.pricePerAxonGateway || 0}
                      onChange={(e) => handleChange('pricePerAxonGateway', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        endAdornment: <InputAdornment position="end">/license</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <AppTextField
                      fullWidth
                      type="number"
                      label="Price per TB Storage"
                      value={formData.pricePerTb || 0}
                      onChange={(e) => handleChange('pricePerTb', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        endAdornment: <InputAdornment position="end">/TB</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Encryption Fee */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Encryption Fee
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.customerManagedEncryption ?? false}
                      onChange={(e) => {
                        handleChange('customerManagedEncryption', e.target.checked);
                        if (!e.target.checked) {
                          handleChange('encryptionFee', 0);
                        }
                      }}
                    />
                  }
                  label="BYOK add-on (customer-managed encryption)"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <AppTextField
                  fullWidth
                  type="number"
                  label="Encryption Fee"
                  value={formData.encryptionFee || 0}
                  onChange={(e) => handleChange('encryptionFee', parseFloat(e.target.value) || 0)}
                  disabled={!formData.customerManagedEncryption}
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    endAdornment: <InputAdornment position="end">{getBillingPeriodLabel()}</InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* CCI module add-ons */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              CCI Module Add-ons
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                CCI Operations is included in the base price. Enable optional modules and set add-on pricing.
                Pricing follows the selected billing cycle ({formData.billingCycle === dt.BILLING_CYCLE.Yearly ? 'yearly' : 'monthly'}).
              </Typography>
            </Alert>
            <Grid container spacing={3}>
              {STRIPE_MODULE_FEATURE_KEYS.map((moduleKey) => (
                <Grid key={moduleKey} size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 260, display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={modules[moduleKey].enabled}
                            onChange={(e) =>
                              setModules({
                                ...modules,
                                [moduleKey]: { ...modules[moduleKey], enabled: e.target.checked },
                              })
                            }
                            disabled={loading}
                          />
                        }
                        label={PRODUCT_CREATOR_MODULE_LABELS[moduleKey]}
                      />
                    </Box>
                    <AppTextField
                      type="number"
                      label="Price"
                      value={modules[moduleKey].price}
                      onChange={(e) =>
                        setModules({
                          ...modules,
                          [moduleKey]: {
                            ...modules[moduleKey],
                            price: parseFloat(e.target.value) || 0,
                          },
                        })
                      }
                      disabled={loading || !modules[moduleKey].enabled}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        endAdornment: <InputAdornment position="end">{getBillingPeriodLabel()}</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 1 }}
                      sx={{ width: 180 }}
                      size="small"
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Price Preview */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Total {formData.billingCycle === dt.BILLING_CYCLE.Yearly ? 'Yearly' : 'Monthly'} Base: <strong>${calculateMonthlyTotal().toFixed(2)}</strong>
            </Typography>
            <Typography variant="caption" display="block" sx={{ opacity: 0.9 }}>
              Base Price: ${formData.monthlyBasePrice.toFixed(2)}
              {(formData.encryptionFee ?? 0) > 0 && ` + Encryption: $${(formData.encryptionFee ?? 0).toFixed(2)}`}
              {Object.values(modules).some((m) => m.enabled && m.price > 0) &&
                ` + Modules: $${Object.values(modules).reduce((sum, m) => sum + (m.enabled ? m.price : 0), 0).toFixed(2)}`
              }
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Plus overage charges based on actual usage
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <AppButton onClick={handleClose} disabled={loading}>
          Cancel
        </AppButton>
        <AppButton
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.name || success}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Creating...' : 'Create Product in Stripe'}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default ProviderStripeProductCreator;
